// 261010 P8c — CHÍNH SÁCH bước Quay phim (thuần: không antd/router, jest chạy được).
// Nút chạy / hộp ghi đè / chữ lỗi / thẻ luật / dòng nhịp. KHÔNG hiện giá. Không nhân bản `eventNeedsCinematography` — lấy `stepState` từ BE.
import type { FilmEvent, FilmRunningJob, FilmShot } from '../../types/film';
import type { StyleOwner, StyleProfileView } from '../../types/filmStyleProfile';
import type { WorkflowStepState } from '../../types/filmWorkflow';
import { BAND_ROWS, OWNER_LABEL } from './styleProfilePolicy';
import { subjectFacingLabel, beatLinkText } from './workflowDetailUtils';
import { planWriteBlock, planWriteLabelOf, workflowActionError, type ActionError, type ConfirmContent } from './workflowActionPolicy';
import type { RuleCardView, RuleRow } from './actingPolicy';

export { beatLinkText };

export type CineBarTone = 'todo' | 'ok' | 'warn' | 'doing' | 'bad';
export interface CinematographyBarCtx {
  eventId: string;
  eventOrder: number;
  stepState: WorkflowStepState;
  shots: Array<Pick<FilmShot, 'screenDirection' | 'durationS'>>;
  jobs: Array<Pick<FilmRunningJob, 'type' | 'eventId' | 'key'>>;
  sending?: boolean;
  editing?: boolean;
  dirty?: boolean;
  errorNote?: string;
}
export interface CinematographyBar {
  chip: { label: string; tone: CineBarTone };
  button: { label: string; enabled: boolean; force: boolean; confirm: boolean; reason?: string; goDirecting?: boolean };
  missingFacing: number;
}
export type NothingToDoFollowup = { kind: 'message'; text: string } | { kind: 'confirm' };

const sameEvent = (j: { eventId?: string | null; key?: string }, eventId: string): boolean =>
  j.eventId === eventId || (!j.eventId && j.key === eventId);

const storyboardBlock = (jobs: CinematographyBarCtx['jobs'], eventId: string): boolean =>
  jobs.some((j) => j.type === 'FILM_STORYBOARD' && sameEvent(j, eventId));

const comma = (n: number): string => n.toFixed(1).replace('.', ',');

export function cinematographyBarOf(ctx: CinematographyBarCtx): CinematographyBar {
  const missingFacing = ctx.shots.filter((s) => subjectFacingLabel(s.screenDirection) === 'chưa có').length;
  if (ctx.shots.length === 0) {
    return {
      chip: { label: 'Chưa có shot', tone: 'todo' },
      button: {
        label: 'Chạy quay phim', enabled: false, force: false, confirm: false, goDirecting: true,
        reason: 'Chưa có shot nào — sang bước 3 Đạo diễn để dựng shot.',
      },
      missingFacing: 0,
    };
  }
  const write = planWriteBlock(ctx.jobs, ctx.eventId);
  let reason: string | undefined;
  if (ctx.editing || ctx.dirty) reason = 'Lưu hoặc huỷ thay đổi trước khi chạy';
  else if (ctx.sending) reason = 'Đang gửi yêu cầu — đợi một chút.';
  else if (ctx.stepState === 'RUNNING') reason = undefined;
  else if (write.blocked) reason = write.reason;
  else if (storyboardBlock(ctx.jobs, ctx.eventId)) reason = 'Video này đang dựng storyboard ở nền — đợi xong rồi chạy quay phim.';

  const disabled = !!reason || ctx.stepState === 'RUNNING';
  const baseBtn = { enabled: !disabled, ...(reason ? { reason } : {}) };

  if (ctx.stepState === 'RUNNING') {
    return {
      chip: { label: 'Đang chạy quay phim', tone: 'doing' },
      button: { ...baseBtn, label: 'Đang chạy…', force: false, confirm: false, enabled: false },
      missingFacing,
    };
  }
  if (ctx.stepState === 'ERROR') {
    return {
      chip: { label: ctx.errorNote ? `Lỗi: ${ctx.errorNote}` : 'Lần chạy gần nhất lỗi', tone: 'bad' },
      button: { ...baseBtn, label: 'Chạy lại', force: false, confirm: false },
      missingFacing,
    };
  }
  if (ctx.stepState === 'READY' || ctx.stepState === 'STALE') {
    return {
      chip: { label: 'Đã có hướng mặt và nhịp', tone: ctx.stepState === 'STALE' ? 'warn' : 'ok' },
      button: { ...baseBtn, label: 'Chạy lại (ghi đè)', force: true, confirm: true },
      missingFacing,
    };
  }
  return {
    chip: { label: `${missingFacing}/${ctx.shots.length} shot chưa có hướng mặt`, tone: 'todo' },
    button: { ...baseBtn, label: 'Chạy quay phim', force: false, confirm: false },
    missingFacing,
  };
}

export function cinematographyConfirmContent(eventOrder: number, hasPromptOrClip: boolean): ConfirmContent {
  const clip = hasPromptOrClip ? ' Prompt và clip đã có của video này có thể thành «cũ».' : '';
  return {
    title: `Chạy lại quay phim cho Video ${eventOrder}?`,
    body: `AI sẽ ghi đè hướng mặt chủ thể và nhịp của mọi shot, kể cả chỗ bạn đã sửa tay.${clip} Không đổi cỡ / góc / máy.`,
    okText: 'Chạy lại',
    cancelText: 'Huỷ',
  };
}

export function cinematographyNothingToDoOf(from: 'NOT_READY' | 'ERROR'): NothingToDoFollowup {
  if (from === 'ERROR') return { kind: 'confirm' };
  return { kind: 'message', text: 'Mọi shot đã có hướng mặt và nhịp — muốn làm lại thì bấm «Chạy lại (ghi đè)».' };
}

export function cinematographyActionError(err: unknown, what: string): ActionError {
  const e = err as { response?: { status?: number; data?: any } };
  const d = e?.response?.data;
  const status = e?.response?.status;
  const code = typeof d?.code === 'string' ? d.code : '';
  const mk = (kind: ActionError['kind'], text: string): ActionError => ({ kind, text: `${what}: ${text}`, reload: true });
  if (code === 'CINEMATOGRAPHY_NOT_READY') return mk('OTHER', 'chưa có shot nào — sang bước 3 Đạo diễn để dựng shot rồi chạy quay phim.');
  if (code === 'NOT_SKY_PLAN') return mk('NOT_SKY', 'plan này không dùng luồng mới — chạy quay phim lẻ chỉ dùng cho plan dùng luồng mới.');
  if (code === 'JOB_RUNNING') return mk('BUSY', `plan đang có việc "${planWriteLabelOf(String(d?.jobType ?? ''))}" chạy ở nền — đợi xong rồi bấm Tải lại, đừng bấm lại.`);
  if (code === 'WORKFLOW_RUN_ACTIVE') return mk('BUSY', 'máy tự chạy chặng đang chạy trên plan này — đợi xong rồi thao tác.');
  if (code === 'NOT_FOUND') return mk('NOT_FOUND', 'không tìm thấy video trong plan này.');
  if (status === 404) return mk('OTHER', 'Máy chủ chưa hỗ trợ chạy quay phim riêng — cập nhật rồi thử lại.');
  if (d?.busy === true && !code) {
    const m = typeof d.message === 'string' && d.message ? ` (${d.message})` : '';
    return mk('BUSY', `việc này đang chạy ở nền${m} — đợi xong rồi bấm Tải lại, đừng bấm lại.`);
  }
  return workflowActionError(err, what);
}

export function cameraRuleCardOf(view: StyleProfileView | null): RuleCardView {
  if (!view) return { state: 'LOADING', note: 'Đang tải hồ sơ phong cách…', rules: [] };
  if (view.status === 'NONE' || !view.profile) return { state: 'NONE', note: 'Truyện chưa có hồ sơ phong cách — chưa có luật nào dành cho Quay phim.', rules: [] };
  if (view.status === 'DRAFT') return { state: 'DRAFT', note: 'Hồ sơ phong cách chưa duyệt — AI chấm bước Quay phim chưa dùng luật (duyệt ở bước 2 Tài sản).', rules: [] };
  const ownersOf = (r: { owners: StyleOwner[] }): StyleOwner[] => r.owners.filter((o) => o === 'CINEMATOGRAPHY');
  const rules: RuleRow[] = view.profile.rules
    .map((r) => ({ r, owners: ownersOf(r) }))
    .filter((x) => x.owners.length > 0)
    .map(({ r, owners }): RuleRow => ({
      id: r.id, title: r.title, when: r.when, then: r.then, owners, ownerText: owners.map((o) => OWNER_LABEL[o]).join(' · '), applied: false,
    }));
  const note = rules.length
    ? 'AI chấm dùng luật này khi chấm; bước chạy không đọc'
    : 'Hồ sơ đã duyệt không có luật nào dành cho Quay phim.';
  return { state: 'APPROVED', note, rules };
}

const durationOf = (ev: Pick<FilmEvent, 'genDurationS' | 'shots'>): number | null => {
  const gen = typeof ev.genDurationS === 'number' && Number.isFinite(ev.genDurationS) && ev.genDurationS > 0 ? ev.genDurationS : null;
  if (gen !== null) return gen;
  const sum = (ev.shots || []).reduce((acc, s) => acc + (typeof s.durationS === 'number' && s.durationS > 0 ? s.durationS : 0), 0);
  return sum > 0 ? sum : null;
};

export function rhythmLineOf(ev: Pick<FilmEvent, 'genDurationS' | 'shots'>, view: StyleProfileView | null): string {
  const shots = ev.shots || [];
  const dur = durationOf(ev);
  if (dur === null || !shots.length) return 'chưa tính được';
  const rate = Math.round((shots.length / dur) * 100) / 10;
  let line = `${shots.length} shot / ${dur} giây = ${comma(rate)} shot/10 giây`;
  const rhythm = view?.status === 'APPROVED' ? view.profile?.rhythm : null;
  if (rhythm?.bands) {
    const bits = BAND_ROWS.map((row) => {
      const b = rhythm.bands[row.key];
      return `${row.label} ${b.lo}–${b.hi}`;
    });
    line += ` · tham khảo: ${bits.join(', ')}`;
  }
  return line;
}
